<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNavigation } from '@/composables/useNavigation'
import { useDefaults } from 'vuetify'
import type { NavigationMenuItem } from './types'
import SubNavDropdown from './SubNavDropdown.vue'
import mdiChevronDown from '~icons/mdi/chevron-down'
import mdiDotsHorizontal from '~icons/mdi/dots-horizontal'

const { t } = useI18n()
const { getMenuItemTo } = useNavigation()

const _props = defineProps<{
  items: NavigationMenuItem[]
  activeItem?: string
}>()

const props = useDefaults(_props)

const emit = defineEmits<{
  select: [item: NavigationMenuItem]
}>()

const isInitialized = ref(false)

onMounted(() => {
  requestAnimationFrame(() => {
    isInitialized.value = true
  })
})

const regularItems = computed(() => props.items.filter((item) => item.locale_name !== 'common.other'))
const otherMenu = computed(() => props.items.find((item) => item.locale_name === 'common.other'))

const activeTabIndex = computed(() => {
  if (props.activeItem == null) return undefined
  const index = regularItems.value.findIndex((item) => item.locale_name === props.activeItem)
  return index >= 0 ? index : undefined
})

const isOtherMenuOpen = ref(false)

function handleSelect(item: NavigationMenuItem) {
  emit('select', item)

  if (item.locale_name === 'common.other') {
    return
  }

  isOtherMenuOpen.value = false
}
</script>

<template>
  <div class="d-flex align-center h-100">
    <v-tabs
      :model-value="activeTabIndex"
      bg-color="primary"
      :slider-color="isInitialized ? 'brand-primary' : 'transparent'"
      color="white"
      height="48"
      class="main-nav"
    >
      <v-tab
        v-for="(item, index) in regularItems"
        :key="item.locale_name"
        :value="index"
        :to="getMenuItemTo(item)"
        :aria-label="t(item.locale_name)"
        :aria-expanded="props.activeItem === item.locale_name && item.sub_menu?.length > 0"
        @click="handleSelect(item)"
      >
        {{ t(item.locale_name) }}
        <v-icon
          v-if="item.sub_menu?.length > 0"
          :icon="mdiChevronDown"
          size="small"
          class="ml-1 chevron-rotate"
          :class="{ 'chevron-rotate--active': props.activeItem === item.locale_name }"
        />
      </v-tab>
    </v-tabs>

    <v-menu
      v-if="otherMenu"
      v-model="isOtherMenuOpen"
      location="bottom start"
      :close-on-content-click="false"
      class="d-none d-lg-flex"
    >
      <template #activator="{ props: menuProps }">
        <v-btn
          v-bind="menuProps"
          variant="text"
          :aria-label="t('common.other')"
          class="h-100 rounded-0"
          @click="handleSelect(otherMenu)"
        >
          <v-icon
            :icon="mdiDotsHorizontal"
            size="x-large"
          />
        </v-btn>
      </template>

      <sub-nav-dropdown :items="otherMenu?.sub_menu ?? []" />
    </v-menu>
  </div>
</template>
